import { Component } from 'react';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import { Col, Row, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import moment from 'moment';

//components
import withLocalization from '../../../hoc/withLocalization';
import applicationRouter from '../../../hoc/applicationRouter';
import CycleDetails from './CycleDetails';
import CopyWeeks from './CopyWeeks';
import { DATE_FORMAT } from './RosterScheduler'
import DayCard from './DayCard';

//elements
import { ProjectsWidget } from '../../../components/GenericForm/Widgets/ProjectsWidget';
import { TasksWidget } from '../../../components/GenericForm/Widgets/TasksWidget';
import LoadingSpinner from '~/elements/LoadingSpinner';
import Button from '~/components/CustomButton/CustomButton.jsx';
import CustomCheckbox from '~/components/CustomCheckbox/CustomCheckbox';
import Select from '../../../elements/Select';
import { CurrentUserGroupsWidget } from '../../../components/GenericForm/Widgets/UserGroupWidget';
import AutoTimeRecordingDays from '~/components/RosterShiftOptions/AutoTimeRecordingDays';
import RosterOverrideSettings from '~/components/RosterShiftOptions/RosterOverrideSettings';

//utils
import { confirmAlert } from 'react-confirm-alert';
import { getDatesFromDurationByWeekDay, getWeekDays, removeEmptyObjects } from '~/utils/commonMethods';
import { isoWeekdayMap } from '~/utils/constants';
import { validateModuleAccess } from '../../../library/stores/userStore';
import { calculateCycleTotals, calculateWeeklyTotals, getCycleStartDate, isDayFixedInOtherWeeks } from '../../../utils/rosterMethods';

//assets
import CloseModalIcon from '../../../assets/img/CloseApproveModalIcon.svg';


const transformSavedDataIntoCycleData = (data, cycleData) => {
    for (const userId in data) {
        const userDates = data[userId];

        for (const date in userDates) {
            const m = moment(date);
            const weekNumber = m.isoWeek();
            const dayName = m.format("dddd").toLowerCase();

            if (cycleData?.[userId]?.[weekNumber] && !cycleData?.[userId]?.[weekNumber]?.[dayName]) {
                cycleData[userId][weekNumber][dayName] = {
                    shiftCodeId: userDates[date].shiftId,
                    fixed: false,
                    date,
                    isPublishedAndEdited: userDates[date].isPublishedAndEdited
                };
            }
        }
    }
    return cycleData;
};

class QuickPlanner extends Component {
    constructor(props) {
        super(props);
        this.state = {
            department: '',
            departmentOptions: [],
            user_group: -1,
            resources: [],
            resourceTemplates: [],
            cycleDetails: {},
            cycleData: {},
            isLoading: false,
            workHoursRuleByDepartment: {},
            selectedUserId: null,
            selectedUserDetails: null,
            selectedWeek: {},
            searchQueryUser: '',
            showCopyWeekModal: false,
            isPublishedMode: false,
            publishedRosterExceptions: {},
            publishedChangedUserIds: {},
            showPublishedChangesModal: false,
            publishedChangesSelections: {},
            publishedChangesAssigningRows: {},
            autoTimesheetDays: { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false },
            overrideShift: false,
            overrideAutoTimeRecording: false,
        }
    }

    componentDidMount() {
        this.loadDepartment()
    }

    loadDepartment = async () => {
        const getDepartments = await this.props.rosterStore.getRuleBasedDepartments({ rule: 'work_hours_rules' });
        const department = this.props.router?.location.state.department;
        const _department = getDepartments?.availableDepartment.find(ele => ele.value === department);
        this.setState({ departmentOptions: getDepartments?.availableDepartment, department: _department ? _department?.value : getDepartments?.availableDepartment[0]?.value }, () => {
            this.loadData();
        })
    }

    loadData = async () => {
        const { clientStore, rosterStore, commonStore, router, userStore, t } = this.props;
        try {
            this.setState({ isLoading: true, user_group: router?.location.state.userGroup || -1 });
            const workHoursRuleByDepartment = await clientStore.getWorkHoursRuleByDepartment(this.state.department);
            const { cycle_week_length, rule_cycle_start_date } = workHoursRuleByDepartment;

            let startDate = '', endDate = ''
            startDate = getCycleStartDate(rule_cycle_start_date, cycle_week_length, moment()).format(DATE_FORMAT);
            endDate = moment(startDate).add(cycle_week_length, 'weeks').add(-1, 'day').format(DATE_FORMAT)

            let pmMembers = '';
            if (userStore?.currentUser.user_type !== 'admin' && userStore?.currentUser?.Manages?.length) {
                pmMembers = userStore.currentUser.Manages.join(',');
            }

            const { resources, employee_rosters } = await rosterStore.getRosterResources({ startDate, endDate, department: this.state.department, isPublished: this.state.isPublishedMode, pmMembers })
            const selectedUserDetails = resources.length ? resources[0] : null;

            this.setState({ workHoursRuleByDepartment, cycleDetails: { startDate, endDate }, resources, resourceTemplates: employee_rosters, selectedUserDetails, selectedUserId: selectedUserDetails?.id }, () => {
                this.setState({ isLoading: false });
                this.initializeCycleData();
            })
        }
        catch (e) {
            console.error(e)
            this.props.router.navigate("/admin/roster")
        }
    }

    getWeekInitialData = () => {
        const { cycleDetails } = this.state;
        const cycleStart = moment(cycleDetails.startDate);
        const cycleEnd = moment(cycleDetails.endDate);
        let weekCursor = cycleStart.clone().startOf('isoWeek');
        const weekNumberData = {};
        while (weekCursor.isSameOrBefore(cycleEnd, 'day')) {
            const weekNumber = weekCursor.isoWeek();
            const weekData = {};
            weekNumberData[weekNumber] = weekData;
            weekCursor.add(1, 'week');
        }
        return weekNumberData;
    }

    initializeCycleData = () => {
        const { cycleDetails, resources } = this.state;
        const cycleStart = moment(cycleDetails.startDate);
        const initializedCycleData = {};
        const userIds = resources.map(r => r.id);
        const weekObj = this.getWeekInitialData()

        userIds.forEach(userId => {
            initializedCycleData[userId] = Object.fromEntries(Object.keys(weekObj).map(week => [week, {}]));
        });

        const getCycleStartWeekNumber = cycleStart.isoWeek();
        const userCycleData = initializedCycleData[this.state.selectedUserId];
        const week = userCycleData ? getCycleStartWeekNumber : null
        const weekEnd = moment(cycleStart).endOf('isoWeek').format('YYYY-MM-DD');

        const weekData = {
            week: week || '',
            start: cycleStart,
            end: weekEnd,
            days: getWeekDays(cycleStart, weekEnd)
        };

        this.setState({ cycleData: initializedCycleData, selectedWeek: weekData }, () => this.loadSavedCycleData());
    };

    loadSavedCycleData = () => {
        const { cycleData, resourceTemplates } = this.state;
        const savedData = {}
        resourceTemplates.forEach(ele => {
            savedData[ele.user_id] = ele.shift_data
        })
        const data = transformSavedDataIntoCycleData(savedData, cycleData)
        this.setState({ cycleData: { ...cycleData, ...data } })
    }

    loadCopyWeekCycleData = (cycleData) => {
        this.setState({ cycleData: { ...cycleData } })
    }

    handleChange = (field, value) => {
        this.setState({ [field]: value, selectedUserId: null, selectedUserDetails: null, isLoading: true }, () =>
            this.loadData()
        );
    }

    handleShiftSelect = (dayKey, shiftDetail, dayDate) => {
        const { selectedWeek, cycleData, selectedUserId, isPublishedMode } = this.state;
        const weekNumber = selectedWeek?.week;
        const weekData = weekNumber ? (cycleData?.[selectedUserId]?.[weekNumber] || {}) : {};
        if (weekData[dayKey]?.source === 'vacation') return;
        const currentShiftCode = weekData[dayKey]?.shiftCodeId;
        const isDeselecting = currentShiftCode === shiftDetail.id;

        const newWeekData = {
            ...weekData,
            [dayKey]: {
                ...(weekData[dayKey] || {}),
                shiftCodeId: isDeselecting ? null : shiftDetail.id,
                fixed: isDeselecting ? false : weekData[dayKey]?.fixed,
                date: dayDate
            }
        };
        if (isDeselecting) delete newWeekData[dayKey]
        this.updateCycleData(weekNumber, newWeekData);
    }

    updateCycleData = (weekNumber, updatedWeekData) => {
        const { selectedUserId, cycleDetails, isPublishedMode, publishedRosterExceptions, publishedChangedUserIds } = this.state;
        this.setState(prevState => {
            const prevCycleData = prevState.cycleData[selectedUserId];
            const currentWeekData = prevCycleData?.[weekNumber] || {};
            const cycleData = { ...prevCycleData, [weekNumber]: updatedWeekData };
            const unfixedDays = Object.keys(currentWeekData).filter(dayKey => currentWeekData[dayKey]?.fixed && (!updatedWeekData[dayKey] || !updatedWeekData[dayKey]?.fixed));

            if (unfixedDays.length > 0) {
                const today = moment().startOf('day');
                for (const weekKey in cycleData) {
                    const weekData = { ...cycleData[weekKey] };
                    let changed = false;

                    for (const dayKey of unfixedDays) {
                        const entry = weekData[dayKey];
                        if (entry && entry.date && (!isPublishedMode || moment(entry.date).isSameOrAfter(today))) {
                            delete weekData[dayKey];
                            changed = true;
                        }
                    }

                    if (changed) {
                        cycleData[weekKey] = weekData;
                    }
                }
            }

            const fixedShifts = {};
            for (const [dayKey, dayData] of Object.entries(updatedWeekData)) {
                if (dayData.fixed && dayData.shiftCodeId) {
                    fixedShifts[dayKey] = dayData.shiftCodeId;
                }


                const prevDayData = currentWeekData[dayKey] || {};
                const isFutureDate = moment(dayData.date).isAfter(moment(), 'day');
                const isCurrentOrFutureDate = !moment(dayData.date).isBefore(moment(), 'day');

                if (isPublishedMode && isCurrentOrFutureDate && selectedUserId) {
                    const shiftChanged = prevDayData.shiftCodeId ? dayData.shiftCodeId !== prevDayData.shiftCodeId : false;
                    const shiftAdded = !prevDayData.shiftCodeId && !!dayData.shiftCodeId;

                    if (shiftChanged && isFutureDate) {
                        const key = `${moment(dayData.date).format('YYYY-MM-DD')}_published_shift_changed`;
                        publishedRosterExceptions[selectedUserId] = { ...publishedRosterExceptions[selectedUserId], [key]: { exception: `${('Published roster shift changed in week')} ${moment(dayData.date).format('MMMM D')}` } };
                    }

                    if (shiftChanged || shiftAdded) {
                        publishedChangedUserIds[selectedUserId] = true;
                    }

                    if (isFutureDate && shiftChanged) {
                        cycleData[weekNumber][dayKey] = { ...dayData, isPublishedAndEdited: true };
                    } else if (isFutureDate && prevDayData.isPublishedAndEdited) {
                        cycleData[weekNumber][dayKey] = { ...dayData, isPublishedAndEdited: true };
                    }
                }
            }

            // Track removed shifts (deselection) in published mode
            if (isPublishedMode && selectedUserId) {
                for (const [dayKey, prevDayData] of Object.entries(currentWeekData)) {
                    if (!updatedWeekData[dayKey] && prevDayData.shiftCodeId) {
                        const isCurrentOrFuture = !moment(prevDayData.date).isBefore(moment(), 'day');
                        if (isCurrentOrFuture) {
                            publishedChangedUserIds[selectedUserId] = true;
                        }
                    }
                }
            }

            const cycleStartYear = moment(cycleDetails.startDate).isoWeekYear();
            const cycleEndYear = moment(cycleDetails.endDate).isoWeekYear();
            for (const [weekKey, weekData] of Object.entries(cycleData)) {
                const updatedWeek = { ...weekData };
                let weekStart = moment().isoWeekYear(cycleStartYear).isoWeek(parseInt(weekKey)).startOf('isoWeek');
                if (cycleEndYear !== cycleStartYear && !weekStart.isBetween(cycleDetails.startDate, cycleDetails.endDate, null, '[]')) {
                    weekStart = moment().isoWeekYear(cycleEndYear).isoWeek(parseInt(weekKey)).startOf('isoWeek');
                }
                const weekEnd = weekStart.clone().endOf('isoWeek');

                for (const [dayKey, shiftCodeId] of Object.entries(fixedShifts)) {
                    const dateDetails = getDatesFromDurationByWeekDay(weekStart, weekEnd, dayKey, isoWeekdayMap)
                    dateDetails.forEach(ele => {
                        if (moment(ele).isBetween(cycleDetails.startDate, cycleDetails.endDate, null, '[]') && (!isPublishedMode || !moment(ele).isBefore(moment(), 'day')) && (!updatedWeek[dayKey] || (updatedWeek[dayKey] && !updatedWeek[dayKey]?.fixed)))
                            updatedWeek[dayKey] = {
                                fixed: false,
                                date: ele,
                                shiftCodeId
                            }
                    })
                }
                cycleData[weekKey] = updatedWeek;
            }
            return { cycleData: { ...prevState.cycleData, [selectedUserId]: cycleData }, publishedRosterExceptions, publishedChangedUserIds };
        });
    };

    handleWeekNavigation = (weekNumber) => {
        const { cycleDetails } = this.state;
        const cycleStartYear = moment(cycleDetails.startDate).isoWeekYear();
        const cycleEndYear = moment(cycleDetails.endDate).isoWeekYear();
        let targetDate = moment().isoWeekYear(cycleStartYear).isoWeek(weekNumber).endOf('isoWeek').format('YYYY-MM-DD');
        if (cycleEndYear !== cycleStartYear && !moment(targetDate).isBetween(cycleDetails.startDate, cycleDetails.endDate, null, '[]')) {
            targetDate = moment().isoWeekYear(cycleEndYear).isoWeek(weekNumber).endOf('isoWeek').format('YYYY-MM-DD');
        }
        this.updateWeekData(targetDate);
    }

    handlePrevWeek = () => {
        const { selectedWeek, cycleDetails } = this.state;
        if (!selectedWeek) return;
        if (moment(cycleDetails.startDate).isSame(selectedWeek.start)) return;
        const prevWeekStart = moment(selectedWeek.start).subtract(1, 'weeks');
        this.updateWeekData(prevWeekStart);
    }

    handleNextWeek = () => {
        const { selectedWeek, cycleDetails } = this.state;
        if (!selectedWeek) return;
        if (moment(cycleDetails.endDate).isSame(selectedWeek.end)) return;
        const nextWeekStart = moment(selectedWeek.start).add(1, 'weeks');
        this.updateWeekData(nextWeekStart);
    }

    updateWeekData = (targetDate) => {
        const { cycleDetails, selectedUserDetails } = this.state;
        if (selectedUserDetails?.has_roster && !selectedUserDetails?.is_quick_planner) return;
        if (selectedUserDetails?.work_pattern_assignment_id && !selectedUserDetails?.has_roster) return;
        const weekNumber = moment(targetDate).isoWeek();
        let weekStart = moment(targetDate).clone().startOf('isoWeek').format('YYYY-MM-DD');
        let weekEnd = moment(targetDate).clone().endOf('isoWeek').format('YYYY-MM-DD');

        const cycleStart = moment(cycleDetails.startDate);
        const cycleEnd = moment(cycleDetails.endDate);

        if (!moment(weekStart).isBetween(cycleStart, cycleEnd, "date", '[]')) {
            weekStart = cycleStart.format('YYYY-MM-DD');
        }

        if (!moment(weekEnd).isBetween(cycleStart, cycleEnd, "date", '[]')) {
            weekEnd = cycleEnd.format('YYYY-MM-DD');
        }

        const weekData = {
            week: weekNumber,
            start: weekStart,
            end: weekEnd,
            days: getWeekDays(weekStart, weekEnd)
        };

        this.setState({ selectedWeek: weekData });
    }

    handleDayToggle = (dayKey, dayDate, dayName) => {
        const { selectedWeek, cycleData, selectedUserId } = this.state;
        const weekNumber = selectedWeek?.week;
        const weekData = weekNumber ? (cycleData[selectedUserId][weekNumber] || {}) : {};
        const isCurrentlyFixed = !!weekData[dayKey]?.fixed;

        const doToggle = () => {
            const newWeekData = {
                ...weekData,
                [dayKey]: {
                    ...(weekData[dayKey] || {}),
                    fixed: !isCurrentlyFixed,
                    date: dayDate
                }
            };
            if (isCurrentlyFixed) delete newWeekData[dayKey];
            this.updateCycleData(weekNumber, newWeekData);
        };

        const { t } = this.props;
        const message = isCurrentlyFixed
            ? t('Deselecting this will result in no shift on') + ` ${t(dayName)} ` + t('for the entire cycle. Are you sure?')
            : t('Selecting this will apply the shift on') + ` ${t(dayName)} ` + t('for the entire cycle. Are you sure?');

        confirmAlert({
            title: t('Confirm'),
            message,
            buttons: [
                { label: t('Yes'), onClick: doToggle },
                { label: t('No'), onClick: () => { } },
            ],
        });
    }

    handleModeChange = () => {
        this.setState({ isPublishedMode: !this.state.isPublishedMode }, () => this.loadData());
    }

    goNext = async () => {
        const { rosterStore, userStore } = this.props;
        const { workHoursRuleByDepartment, cycleDetails } = this.state;
        const { cycle_week_length } = workHoursRuleByDepartment;
        const _startDate = moment(cycleDetails.endDate).add(1, 'day').format('YYYY-MM-DD');
        const _endDate = moment(_startDate).add(cycle_week_length, 'weeks').add(-1, 'day').format('YYYY-MM-DD');

        this.setState({ isLoading: true });

        let pmMembers = '';
        if (userStore?.currentUser.user_type !== 'admin' && userStore?.currentUser?.Manages?.length) {
            pmMembers = userStore.currentUser.Manages.join(',');
        }

        const { resources, employee_rosters } = await rosterStore.getRosterResources({
            startDate: _startDate, endDate: _endDate,
            department: this.state.department,
            isPublished: this.state.isPublishedMode,
            pmMembers
        });
        const selectedUserDetails = resources.length ? resources[0] : null;

        this.setState({
            cycleDetails: { startDate: _startDate, endDate: _endDate },
            resources,
            resourceTemplates: employee_rosters,
            selectedUserDetails,
            selectedUserId: selectedUserDetails?.id,
            isLoading: false
        }, () => {
            this.initializeCycleData();
        });
    };

    goPrev = async () => {
        const { rosterStore, userStore } = this.props;
        const { workHoursRuleByDepartment, cycleDetails } = this.state;
        const { cycle_week_length, rule_cycle_start_date } = workHoursRuleByDepartment;
        const _startDate = moment(cycleDetails.startDate).subtract(cycle_week_length, 'weeks').format('YYYY-MM-DD');
        const _endDate = moment(cycleDetails.startDate).subtract(1, 'day').format('YYYY-MM-DD');

        if (moment(_startDate).isBefore(rule_cycle_start_date)) return;

        this.setState({ isLoading: true });

        let pmMembers = '';
        if (userStore?.currentUser.user_type !== 'admin' && userStore?.currentUser?.Manages?.length) {
            pmMembers = userStore.currentUser.Manages.join(',');
        }

        const { resources, employee_rosters } = await rosterStore.getRosterResources({
            startDate: _startDate, endDate: _endDate,
            department: this.state.department,
            isPublished: this.state.isPublishedMode,
            pmMembers
        });
        const selectedUserDetails = resources.length ? resources[0] : null;

        this.setState({
            cycleDetails: { startDate: _startDate, endDate: _endDate },
            resources,
            resourceTemplates: employee_rosters,
            selectedUserDetails,
            selectedUserId: selectedUserDetails?.id,
            isLoading: false
        }, () => {
            this.initializeCycleData();
        });
    };

    handleEndPatternAndContinue = async (userId) => {
        const { rosterStore, commonStore, t } = this.props;
        const { cycleDetails, resources } = this.state;
        const resource = resources.find(r => r.id === userId);
        if (!resource || !resource.work_pattern_assignment_id) return;
        try {
            const dayBeforeCycle = moment(cycleDetails.startDate).subtract(1, 'day');
            const assignmentStart = resource.work_pattern_assignment_start_date ? moment(resource.work_pattern_assignment_start_date) : dayBeforeCycle;
            const endDate = moment.max(dayBeforeCycle, assignmentStart).format('YYYY-MM-DD');
            const response = await rosterStore.updateAssignmentEndDate(resource.work_pattern_assignment_id, { end_date: endDate });
            commonStore.addNotification(response.message || t('Assignment end date updated successfully.'), null, 'success');
            const updatedResources = resources.map(r => r.id === userId ? Object.assign({}, r, { work_pattern_assignment_id: null }) : r);
            const updatedSelected = updatedResources.find(r => r.id === this.state.selectedUserId);
            this.setState({ resources: updatedResources, selectedUserDetails: updatedSelected });
        } catch (err) {
            commonStore.addNotification((err && err.message) || t('Failed to end pattern'), null, 'error');
        }
    }

    handleSave = () => {
        const { publishedChangedUserIds, isPublishedMode, cycleData, resourceTemplates, resources } = this.state;
        const { commonStore, t } = this.props;

        if (isPublishedMode && resourceTemplates.length > 0) {
            const plannedUserIds = resourceTemplates.map(ele => ele.user_id);
            for (const userId of plannedUserIds) {
                const userCycleData = cycleData[userId] || {};
                const hasAnyShift = Object.values(userCycleData).some(weekData =>
                    Object.values(weekData || {}).some(dayData => dayData?.shiftCodeId)
                );
                if (!hasAnyShift) {
                    const user = resources.find(r => String(r.id) === String(userId));
                    const userName = user?.name || userId;
                    commonStore.addNotification(`${userName} ${t('must have one selected shifts in a cycle to save')}`, null, 'error');
                    return;
                }
            }
        }

        if (isPublishedMode && Object.keys(publishedChangedUserIds).length > 0) {
            this.setState({ showPublishedChangesModal: true });
            return;
        }
        this.executeSave();
    }

    handlePublishedChangesSelection = (userId, field, value, option) => {
        const labelField = field === 'project_id' ? 'project_label' : field === 'task_id' ? 'task_label' : null;
        this.setState(prev => ({
            publishedChangesSelections: {
                ...prev.publishedChangesSelections,
                [userId]: {
                    ...(prev.publishedChangesSelections[userId] || {}),
                    [field]: value,
                    ...(labelField ? { [labelField]: option?.label || null } : {}),
                }
            }
        }));
    }

    togglePublishedChangesAssignRow = (userId) => {
        this.setState(prev => {
            const isCurrentlyOpen = !!prev.publishedChangesAssigningRows[userId];
            return {
                publishedChangesAssigningRows: isCurrentlyOpen ? {} : { [userId]: true }
            };
        });
    }

    handleClearPublishedChangesRow = (userId) => {
        this.setState(prev => {
            const publishedChangesSelections = { ...prev.publishedChangesSelections };
            delete publishedChangesSelections[userId];
            return { publishedChangesSelections };
        });
    }

    handlePublishedChangesProceed = () => {
        this.setState({ showPublishedChangesModal: false }, () => this.executeSave());
    }

    handleAutoTimesheetDaysChange = (autoTimesheetDays) => {
        this.setState({ autoTimesheetDays });
    }

    handleOverrideChange = ({ overrideShift, overrideAutoTimeRecording }) => {
        this.setState({ overrideShift, overrideAutoTimeRecording });
    }

    executeSave = async () => {
        const { cycleData, workHoursRuleByDepartment, cycleDetails, resourceTemplates, publishedRosterExceptions, isPublishedMode, publishedChangesSelections, autoTimesheetDays, overrideShift, overrideAutoTimeRecording } = this.state;
        const { rosterStore, commonStore } = this.props;
        this.setState({ loading: true });
        const data = removeEmptyObjects(cycleData);
        const planned_shift_details = {}
        const getAlreadyPlannedUsers = resourceTemplates.map(ele => ele.user_id);

        if (getAlreadyPlannedUsers.length) {
            getAlreadyPlannedUsers.forEach(ele => {
                if (data[ele] && Object.keys(data[ele]).length > 0)
                    planned_shift_details[ele] = data[ele];
                else
                    planned_shift_details[ele] = {};
                delete data[ele];
            })
        }

        const payload = {
            cycle_start_date: moment(cycleDetails?.startDate).format('YYYY-MM-DD'),
            cycle_end_date: moment(cycleDetails?.endDate).format('YYYY-MM-DD'),
            shift_details: data,
            rules: { ...workHoursRuleByDepartment },
            planned_shift_details,
        }

        if (isPublishedMode) {
            payload['published_roster_exceptions'] = publishedRosterExceptions;
            payload['published_changes_assignments'] = publishedChangesSelections;
            payload['autoTimesheetDays'] = autoTimesheetDays;
            payload['overrideShift'] = overrideShift;
            payload['overrideAutoTimeRecording'] = overrideAutoTimeRecording;
        }

        try {
            const savedTemplate = await rosterStore.createRosterWithQuickPlanner(payload)
            if (savedTemplate) {
                commonStore.addNotification((savedTemplate.message), null, 'success');
                this.props.router.navigate('/admin/roster');
            }
        } catch (err) {
            console.error(err);
        } finally {
            this.setState({ loading: false });
        }

    }

    render() {
        const { commonStore, t, userStore } = this.props;
        const { isLoading, selectedUserId, selectedUserDetails, selectedWeek, cycleData, cycleDetails, showCopyWeekModal, isPublishedMode,
            workHoursRuleByDepartment, department, user_group, resources, departmentOptions } = this.state;
        const { mandatory_overtime_limit_per_week = 0, cycle_week_length, rule_cycle_start_date } = workHoursRuleByDepartment;
        if (!validateModuleAccess('md_QuickPlanner', 'view')) return null;
        const canQuickPlannerEdit = validateModuleAccess('md_QuickPlanner', 'createAndEdit');
        const canRosterEdit = validateModuleAccess('md_Roster', 'createAndEdit');
        const canWorkPatternManage = validateModuleAccess('md_WorkPattern', 'manageWorkPatterns');
        const isMember = userStore && userStore.currentUser && userStore.currentUser.user_type === 'member';
        const currentUserId = userStore && userStore.currentUser && userStore.currentUser.id;
        const eligibleCopyUsers = resources.filter(r => ((!r.has_roster) || (r.has_roster && r.is_quick_planner)) && !(r.work_pattern_assignment_id && !r.has_roster));
        const copyUsers = isMember ? eligibleCopyUsers.filter(r => r.id === currentUserId) : eligibleCopyUsers;
        if (!selectedWeek) return null;
        if (isLoading) return <LoadingSpinner />;
        const companyShifts = commonStore.sortCompanyShifts(commonStore?.config.companyShifts, false, { F1: false, F2: false, F3: false, F4: false, F5: false });
        const weekDays = selectedWeek?.days || [];
        const weekNumber = selectedWeek?.week;
        const weekData = weekNumber ? (cycleData?.[selectedUserId]?.[weekNumber] || {}) : {};

        const dateTimeRules = commonStore.getDateTimeRules();
        const cycleTotals = calculateWeeklyTotals(cycleData[selectedUserId], commonStore, cycleDetails);
        const cycleTotalsSummary = calculateCycleTotals(cycleTotals, cycleDetails);
        const calculatedEmployementHours = (selectedUserDetails?.employee_percent * selectedUserDetails?.work_time_agreement) / 100
        const totalLimitAccordingToCycleLength = (calculatedEmployementHours * cycle_week_length).toFixed(dateTimeRules.decimal_places || 2);
        const maxCycleLimitBalance = (totalLimitAccordingToCycleLength - parseFloat(cycleTotalsSummary.totalHours)).toFixed(dateTimeRules.decimal_places || 2);
        const _startDate = moment(cycleDetails.startDate).add(-cycle_week_length, 'weeks').format('YYYY-MM-DD')
        const isDateSameOrBeforeCycleStartDate = moment(_startDate).isBefore(rule_cycle_start_date);

        let _resources = resources;
        if (user_group > 0) {
            _resources = resources.filter((r) => r.user_groups.indexOf(parseInt(user_group, 10)) >= 0);
        }

        return (
            <>
                <Row>
                    <Col md={8}>
                        <div className="d-flex justify-content-start align-items-center fs14">
                            <div className='d-flex flex-column justify-content-center align-items-center cycle-preview-navigator'>
                                <div className='text-muted fw-600 fs12'>
                                    {t('Cycle')}
                                </div>
                                <div className='d-flex justify-content-between align-items-center'>
                                    <span
                                        className='p-1'
                                        style={{ cursor: isDateSameOrBeforeCycleStartDate ? 'not-allowed' : 'pointer', marginTop: '-10px' }}
                                        onClick={this.goPrev}
                                    >
                                        <b className="scheduleCaret rotate-90 m-1"></b>
                                    </span>

                                    <div style={{ padding: '4px 14px' }}>
                                        <div className='d-flex align-items-baseline gap-1'>
                                            <span className="text-capitalize fw-600 fs15">
                                                {moment(cycleDetails?.startDate).format('MMMM DD')}
                                            </span>
                                            <span className='fw-600 fs12 color-2550AC cycle-preview-year'>
                                                {moment(cycleDetails?.startDate).format('YYYY')}
                                            </span>
                                        </div>
                                    </div>

                                    <div style={{ padding: '4px 14px' }}>
                                        <div className="text-capitalize d-flex align-items-baseline gap-1">
                                            <span className="fw-600 fs15">
                                                {moment(cycleDetails?.endDate).format('MMMM DD')}
                                            </span>
                                            <span className='fw-600 fs12 color-2550AC cycle-preview-year'>
                                                {moment(cycleDetails?.endDate).format('YYYY')}
                                            </span>
                                        </div>
                                    </div>

                                    <span className='p-1 cursor-pointer' style={{ marginTop: '-10px' }} onClick={this.goNext}>
                                        <b className="scheduleCaret rotate-270 m-1"></b>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div className='d-flex justify-content-end gap-2'>
                            <Button md onClick={() => this.props.router.navigate('/admin/roster')}>{t('Cancel')}</Button>
                            {canQuickPlannerEdit && <Button fill dynamic_lg disabled={!_resources.length} onClick={() => this.handleSave()}>{t('Save')}</Button>}
                        </div>
                    </Col>
                </Row>
                <Row className='mt-4'>
                    <Col xs={2}>
                        <label className="control-label mb-1 fs15">{t('Department')}</label>
                        <Select
                            placeholder={t('Select...')}
                            options={departmentOptions}
                            value={departmentOptions.find(ele => ele.value === department)}
                            onChange={e => this.handleChange('department', e?.value)}
                        />
                    </Col>
                    <Col xs={2}>
                        <label className="control-label mb-1">
                            {`${t('Filter by')} ${t('Group')}`}
                        </label>
                        <CurrentUserGroupsWidget
                            value={user_group}
                            onChange={value => this.setState({ user_group: value })}
                        />
                    </Col>
                    <Col xs={5} className='d-flex align-items-end'>
                        <div className="d-flex justify-content-center gap-2">
                            <Button
                                fill dynamic_lg
                                onClick={this.handlePrevWeek}
                                disabled={moment(cycleDetails.startDate).isSame(selectedWeek.start)}
                            >
                                <i className="fas fa-chevron-left"></i>
                                <span className="ms-2">{t('Prev')}</span>
                            </Button>
                            <div style={{ background: '#bcc2c4', borderRadius: 8 }}>
                                <span className="fw-bold d-flex align-items-center m-2 mt-2 fs15" style={{ background: 'white', border: '4px solid #fff', borderRadius: 5 }}>
                                    {t('Week')} {selectedWeek.week} : {moment(selectedWeek.start).format(dateTimeRules.short_date_format)} - {moment(selectedWeek.end).format(dateTimeRules.short_date_format)}
                                </span>
                            </div>
                            <Button
                                fill dynamic_lg
                                onClick={this.handleNextWeek}
                                disabled={moment(cycleDetails.endDate).isSame(selectedWeek.end) || !_resources.length}
                            >
                                <span className="me-2">{t('Next')}</span>
                                <i className="fas fa-chevron-right"></i>
                            </Button>
                        </div>
                    </Col>
                    <Col xs={3} className='d-flex justify-content-end align-items-center gap-2'>
                        <div className="tabContainer d-flex justify-content-end w-75">
                            <button
                                className={!isPublishedMode ? 'tablink tablink__active-left' : 'tablink tablink__left'}
                                onClick={() => isPublishedMode && this.handleModeChange()}
                            >
                                {t('Active')}
                            </button>
                            <button
                                className={isPublishedMode ? 'tablink tablink__active-right' : 'tablink tablink__right'}
                                onClick={() => !isPublishedMode && this.handleModeChange()}
                            >
                                {t('Published')}
                            </button>
                        </div>
                        {canQuickPlannerEdit && <Button
                            disabled={!_resources.length}
                            onClick={() => this.setState({ showCopyWeekModal: true })}
                        >
                            <i className="fas fa-copy"></i>
                        </Button>}
                    </Col>
                </Row>
                {_resources.length ?
                    <>
                        <Row className='d-flex mt-2'>
                            <div className='d-flex justify-content-end fw-600'>
                                {t('Total')} {t('Cycle')} {`${totalLimitAccordingToCycleLength}${t('hr')}`} | {t('Left')} <span className={`fw-600 ms-1 ${maxCycleLimitBalance < 0 ? 'average-hours-high' : ""}`}>{`${maxCycleLimitBalance}${t('hr')}`}</span>
                            </div>
                        </Row>
                        <Row>
                            <Col xs={2} className="mt-3 pe-0">
                                <div className="weekly-shift-weeks-container">
                                    <div className="d-flex justify-content-start align-items-center p-2 weekly-shift-weeks-header">
                                        <span className="fw-bold fs14">
                                            {t('Members')} ({this.state.resources.length})
                                        </span>
                                    </div>
                                    <div className="quick-planner-weeks-body">
                                        <div className="conversation-search mb-2">
                                            <input
                                                type="text"
                                                className="conversation-search-input"
                                                placeholder={t('Search')}
                                                onChange={(e) => this.setState({ searchQueryUser: e.target.value })}
                                                value={this.state.searchQueryUser}
                                            />
                                            <i className="fa fa-search conversation-search-icon" />
                                        </div>
                                        <div className="d-flex flex-column gap-2">
                                            {_resources.filter(member => member.name.toLowerCase().includes(this.state.searchQueryUser.toLowerCase())).map((member, index) => (
                                                <button
                                                    key={`Cycle week-${index}`}
                                                    className="weekly-shift-week-navigation-btn text-primary-color"
                                                    onClick={() => this.setState({ selectedUserId: member.id, selectedUserDetails: member })}
                                                >
                                                    <span
                                                        className={`fw-600 fs12 member-name ${member.id === selectedUserId ? 'active-bg text-light' : ''}`}
                                                    >
                                                        {(member?.has_roster && !member.is_quick_planner) || (member?.work_pattern_assignment_id && !member.has_roster) ? '⛔' : null}
                                                        <span className="truncate fw-600 fs14"> {member.name} </span>
                                                        {member.employee_percent ? ` | ${member.employee_percent}%` : ''}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={8} className="mt-3 pe-0">
                                <div className="modal-container overflow-auto" style={{ height: '75vh' }}>
                                    {selectedUserDetails?.work_pattern_assignment_id && !selectedUserDetails?.has_roster ? (
                                        <div className="d-flex flex-column align-items-center justify-content-center h-100 gap-3">
                                            <i className="fa fa-info-circle text-primary" style={{ fontSize: '2rem' }} />
                                            <p className="text-center fs-14 mb-0">
                                                {t('This member has an active work pattern assignment. Please end the assignment before creating a roster in Quick Planner.')}
                                            </p>
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip id="tooltip-end-assignment">{canWorkPatternManage ? t('End Existing Assignments') : t("You don't have enough rights to perform this action")}</Tooltip>}
                                            >
                                                <span>
                                                    <Button
                                                        fill
                                                        disabled={!canWorkPatternManage}
                                                        onClick={() => canWorkPatternManage && confirmAlert({
                                                            title: `${t('Work Pattern')} ${t('Already')} ${t('Assigned')}`,
                                                            message: t('This employee already has an active work pattern. Please end it before creating a new roster.'),
                                                            buttons: [
                                                                { label: `${t('End')} ${t('Existing')} ${t('Assignments')}`, onClick: () => this.handleEndPatternAndContinue(selectedUserDetails.id) },
                                                                { label: t('Cancel'), onClick: () => { } },
                                                            ],
                                                        })}
                                                    >
                                                        {t('End')} {t('Existing')} {t('Assignments')}
                                                    </Button>
                                                </span>
                                            </OverlayTrigger>
                                        </div>
                                    ) : selectedUserDetails?.has_roster && !selectedUserDetails?.is_quick_planner ? (
                                        <div className="d-flex flex-column align-items-center justify-content-center h-100 gap-3">
                                            <i className="fa fa-info-circle text-primary" style={{ fontSize: '2rem' }} />
                                            <p className="text-center fs-14 mb-0">
                                                {t('This member has been created by individual roster. You can edit the shifts for this member in the individual roster view.')}
                                            </p>
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip id="tooltip-open-roster">{canRosterEdit ? t('Open Individual Roster') : t("You don't have enough rights to perform this action")}</Tooltip>}
                                            >
                                                <span>
                                                    <Button
                                                        fill
                                                        disabled={!canRosterEdit}
                                                        onClick={() => canRosterEdit && this.props.router.navigate('/admin/roster/add', { state: { userId: selectedUserDetails.id, eventDate: moment().format('YYYY-MM-DD') } })}
                                                    >
                                                        {t('Open Individual Roster')}
                                                    </Button>
                                                </span>
                                            </OverlayTrigger>
                                        </div>
                                    ) : (
                                        <div className="row me-0">
                                            {weekDays.map((day) => {
                                                const isFixedInOtherWeeks = isDayFixedInOtherWeeks(day.key, cycleData[selectedUserId], weekNumber);
                                                const isFixedInThisWeek = weekData[day.key]?.fixed;
                                                const dayDate = day.date;
                                                const isDepartmentChange = selectedUserDetails?.department_updated_on && moment(dayDate).isSameOrBefore(moment(selectedUserDetails.department_updated_on), 'day');
                                                const isVacation = weekData[day.key]?.source === 'vacation';
                                                const isDisabled = (isFixedInOtherWeeks && !isFixedInThisWeek) || (isPublishedMode && moment(day.date).isBefore(moment(), 'day')) || !!isDepartmentChange || !canQuickPlannerEdit || isVacation;
                                                const dayDateFormatted = moment(day.date).format('YYYY-MM-DD');
                                                const isHoliday = commonStore.isHoliday(dayDateFormatted);
                                                const holidayDetails = isHoliday ? commonStore.getHolidayDetails(dayDateFormatted) : null;
                                                const dayLabel = `${t((day?.dayName?.slice(0, 3)))} (${moment(day.date).format(dateTimeRules.short_date_format)})`;

                                                return (
                                                    <DayCard
                                                        day={day}
                                                        dayDate={dayDate}
                                                        dayLabel={dayLabel}
                                                        isFixedInOtherWeeks={isFixedInOtherWeeks}
                                                        isFixedInThisWeek={isFixedInThisWeek}
                                                        isHoliday={isHoliday}
                                                        // isRosterPublished={isPublishedMode}
                                                        holidayDetails={holidayDetails}
                                                        isDisabled={isDisabled}
                                                        weekData={weekData}
                                                        companyShifts={companyShifts}
                                                        handleShiftSelect={this.handleShiftSelect}
                                                        handleDayToggle={this.handleDayToggle}
                                                        addHelperText={(isVacation && t("This day is on hold for vacation and cannot be assigned a shift.")) || (isDepartmentChange && t("This day's shifts are locked due to a department change."))}
                                                    />
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </Col>
                            <Col xs={2} className="mt-3">
                                <div>
                                    <CycleDetails
                                        cycleTotalsSummary={cycleTotalsSummary}
                                        cycleTotals={cycleTotals}
                                        selectedWeek={selectedWeek}
                                        workHoursRuleByDepartment={workHoursRuleByDepartment}
                                        mandatory_overtime_limit_per_week={mandatory_overtime_limit_per_week}
                                        handleWeekNavigation={this.handleWeekNavigation}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </>
                    :
                    <Row className='w-100 d-flex justify-content-center mt-100 fw-bold'>
                        {t('No members found! Clear the filter if any applied')}.
                    </Row>
                }
                {showCopyWeekModal &&
                    <CopyWeeks
                        cycleDetails={cycleDetails}
                        closeModal={() => this.setState({ showCopyWeekModal: false })}
                        cycleData={cycleData}
                        users={copyUsers}
                        isSingleCopy={isMember}
                        updateCycleData={this.loadCopyWeekCycleData}
                    />
                }
                {this.state.showPublishedChangesModal && (() => {
                    const { publishedChangedUserIds, publishedChangesSelections, publishedChangesAssigningRows, resources, autoTimesheetDays, overrideShift, overrideAutoTimeRecording } = this.state;
                    const affectedUserIds = Object.keys(publishedChangedUserIds);
                    const clientData = commonStore?.config?.client?.data;
                    const allowOverride = !!clientData?.basicRules?.workPlanSettings?.allow_optional_roster_shift_override;
                    const allowShiftAutoTimesheetToggle = !!clientData?.loginRules?.allowShiftToEnableDisableAutoTimesheet;
                    return (
                        <Modal
                            size="lg"
                            centered
                            show={this.state.showPublishedChangesModal}
                            onHide={() => this.setState({ showPublishedChangesModal: false })}
                        >
                            <Modal.Header className="set_ModalHeader">
                                <Modal.Title className='w-100 text-center custom-modal-title'>
                                    {t('Published Roster Changes')}
                                </Modal.Title>
                                <img
                                    src={CloseModalIcon}
                                    className="cursor-pointer"
                                    onClick={() => this.setState({ showPublishedChangesModal: false })}
                                    alt='close-btn'
                                />
                            </Modal.Header>
                            <Modal.Body style={{ maxHeight: '60vh', minHeight: '50vh', overflowY: 'auto' }}>
                                <div className="d-flex flex-column gap-3">
                                    {affectedUserIds.map((userId, idx) => {
                                        const user = resources.find(r => String(r.id) === String(userId));
                                        const isAssigning = !!publishedChangesAssigningRows[userId];
                                        const selection = publishedChangesSelections[userId] || {};
                                        return (
                                            <div key={userId} className="d-flex align-items-center justify-content-between fs-14 gap-3 flex-wrap"
                                                style={{
                                                    padding: '7px 14px',
                                                    borderTop: idx > 0 ? '1px solid #f0f0f0' : undefined,
                                                    background: idx % 2 === 0 ? '#f8f9fa' : '#fff'
                                                }}>
                                                <span className="fw-600" style={{ minWidth: 140 }}>
                                                    {user?.name || userId}
                                                </span>
                                                {isAssigning ? (
                                                    <>
                                                        <div style={{ flex: 1, minWidth: 160 }}>
                                                            <ProjectsWidget
                                                                value={selection.project_id || null}
                                                                onChange={(v, opt) => this.handlePublishedChangesSelection(userId, 'project_id', v, opt)}
                                                                applyOnFirstLoad
                                                                filterByUser={Number(userId)}
                                                                menuPlacement="auto"
                                                                maxMenuHeight={150}
                                                            />
                                                        </div>
                                                        <div style={{ flex: 1, minWidth: 160 }}>
                                                            <TasksWidget
                                                                className="form-control"
                                                                onChange={(v, opt) => this.handlePublishedChangesSelection(userId, 'task_id', v, opt)}
                                                                value={selection.task_id || null}
                                                                defaultIfOne
                                                                defaultByGeneric
                                                                filterByUser={Number(userId)}
                                                                filterByProject={selection.project_id}
                                                                menuPlacement="auto"
                                                                maxMenuHeight={150}
                                                            />
                                                        </div>
                                                        <span
                                                            role="button"
                                                            className="projectassignbtn m-1"
                                                            onClick={() => this.togglePublishedChangesAssignRow(userId)}
                                                        >
                                                            {t('Done')}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <div className="d-flex align-items-center flex-wrap" style={{ gap: 6 }}>
                                                        {(selection.project_id || selection.task_id) && (
                                                            <span className="fs-12 text-muted">
                                                                {selection.project_label && (
                                                                    <span className="fw-600">{selection.project_label}</span>
                                                                )}
                                                                {selection.project_label && selection.task_label && (
                                                                    <span className="mx-1">·</span>
                                                                )}
                                                                {selection.task_label && (
                                                                    <span>{selection.task_label}</span>
                                                                )}
                                                            </span>
                                                        )}
                                                        <span
                                                            role="button"
                                                            className="projectassignbtn m-1"
                                                            onClick={() => this.togglePublishedChangesAssignRow(userId)}
                                                        >
                                                            {selection.project_id || selection.task_id
                                                                ? t('Edit')
                                                                : `${t('Assign')} ${t('Project')} / ${t('Task')}`
                                                            }
                                                        </span>
                                                        {(selection.project_id || selection.task_id) && (
                                                            <span
                                                                role="button"
                                                                className="projectassignbtn m-1"
                                                                onClick={() => this.handleClearPublishedChangesRow(userId)}
                                                            >
                                                                {t('Clear')}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </Modal.Body>
                            <div className="px-3 pb-2">
                                {(allowShiftAutoTimesheetToggle || allowOverride) && <hr className="m-0" />}
                                {allowShiftAutoTimesheetToggle && (
                                    <AutoTimeRecordingDays
                                        autoTimesheetDays={autoTimesheetDays}
                                        onChange={this.handleAutoTimesheetDaysChange}
                                    />
                                )}
                                {allowOverride && (
                                    <RosterOverrideSettings
                                        overrideShift={overrideShift}
                                        overrideAutoTimeRecording={overrideAutoTimeRecording}
                                        onChange={this.handleOverrideChange}
                                    />
                                )}
                            </div>
                            <Modal.Footer className="d-flex justify-content-end gap-2">
                                <Button fill md onClick={this.handlePublishedChangesProceed}>{t('Proceed')}</Button>
                                <Button onClick={() => this.setState({ showPublishedChangesModal: false })}>{t('Close')}</Button>
                            </Modal.Footer>
                        </Modal>
                    );
                })()}
            </>
        );
    }
}

export default inject('userStore', 'commonStore', 'scheduleStore', 'clientStore', 'rosterStore')(withLocalization(applicationRouter(observer(QuickPlanner))));